import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Grid, Card, CardContent, Chip, Stack,
  CircularProgress, Alert, Divider,
} from "@mui/material";
import { getAdminAnalyticsSummary } from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";

const ENTITY_CONFIG = [
  {
    key: "users",
    label: "Users",
    listRoute: frontendRoutes.ADMIN_USERS,
    paramName: "accountStatus",
    statusOrder: ["ACTIVE", "SUSPENDED", "DEACTIVATED"],
    statusColor: { ACTIVE: "success", SUSPENDED: "warning", DEACTIVATED: "error" },
  },
  {
    key: "shops",
    label: "Shops",
    listRoute: frontendRoutes.ADMIN_SHOPS,
    paramName: "status",
    statusOrder: ["ACTIVE", "SUSPENDED", "CLOSED"],
    statusColor: { ACTIVE: "success", SUSPENDED: "warning", CLOSED: "default" },
  },
  {
    key: "products",
    label: "Products",
    listRoute: frontendRoutes.ADMIN_PRODUCTS,
    paramName: "status",
    statusOrder: ["DRAFT", "PUBLISHED", "ARCHIVED", "OUT_OF_STOCK", "SUSPENDED"],
    statusColor: { DRAFT: "default", PUBLISHED: "success", ARCHIVED: "default", OUT_OF_STOCK: "warning", SUSPENDED: "error" },
  },
  {
    key: "orders",
    label: "Orders",
    listRoute: frontendRoutes.ADMIN_ORDERS,
    paramName: "status",
    statusOrder: [
      "PENDING", "IN_PROGRESS", "REJECTED", "IN_TRANSIT", "READY_FOR_PICKUP",
      "DELIVERED", "FULFILLED", "CANCELLED", "RETURNED", "REFUNDED", "DISPUTED",
    ],
    statusColor: {
      PENDING: "default", IN_PROGRESS: "info", REJECTED: "error", IN_TRANSIT: "info",
      READY_FOR_PICKUP: "info", DELIVERED: "primary", FULFILLED: "success",
      CANCELLED: "default", RETURNED: "warning", REFUNDED: "warning", DISPUTED: "error",
    },
  },
  {
    key: "reports",
    label: "Reports",
    listRoute: frontendRoutes.ADMIN_REPORTS,
    paramName: "status",
    statusOrder: ["PENDING", "REVIEWED", "DISMISSED", "ACTIONED"],
    statusColor: { PENDING: "warning", REVIEWED: "info", DISMISSED: "default", ACTIONED: "success" },
  },
  {
    key: "verifications",
    label: "Identity Verifications",
    listRoute: frontendRoutes.ADMIN_VERIFICATIONS,
    paramName: "status",
    // VerificationList's queue only supports these three tabs — NOT_SUBMITTED
    // users never submitted anything, so there's no review queue for it.
    statusOrder: ["PENDING", "APPROVED", "REJECTED"],
    nonLinkableStatuses: ["NOT_SUBMITTED"],
    statusColor: { NOT_SUBMITTED: "default", PENDING: "warning", APPROVED: "success", REJECTED: "error" },
  },
];

const StatRow = ({ label, count, color, onClick }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    py={0.5}
    onClick={onClick}
    sx={onClick ? { cursor: "pointer", "&:hover": { opacity: 0.7 } } : undefined}
  >
    <Chip label={label.replace(/_/g, " ")} size="small" color={color || "default"} />
    <Typography variant="body2" fontWeight={600}>{count ?? 0}</Typography>
  </Stack>
);

const EntityCard = ({ config, breakdown, navigate }) => {
  const total = breakdown?.total || 0;
  const byStatus = breakdown?.byStatus || {};
  const extraKeys = Object.keys(byStatus).filter((k) => !config.statusOrder.includes(k) && !config.nonLinkableStatuses?.includes(k));

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Card variant="outlined" sx={{ height: "100%" }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">{config.label}</Typography>
          <Typography variant="h4" fontWeight={700} mb={1}>{total}</Typography>
          <Divider sx={{ mb: 1 }} />
          {config.statusOrder.map((status) => (
            <StatRow
              key={status}
              label={status}
              count={byStatus[status]}
              color={config.statusColor[status]}
              onClick={() => navigate(`${config.listRoute}?${config.paramName}=${status}`)}
            />
          ))}
          {config.nonLinkableStatuses?.map((status) => (
            <StatRow key={status} label={status} count={byStatus[status]} color={config.statusColor[status]} />
          ))}
          {extraKeys.map((status) => (
            <StatRow
              key={status}
              label={status}
              count={byStatus[status]}
              onClick={() => navigate(`${config.listRoute}?${config.paramName}=${status}`)}
            />
          ))}
        </CardContent>
      </Card>
    </Grid>
  );
};

const DisputesCard = ({ disputes, navigate }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">Disputes</Typography>
        <Typography variant="h4" fontWeight={700} mb={1}>{disputes?.total || 0}</Typography>
        <Divider sx={{ mb: 1 }} />
        <StatRow label="OPEN" count={disputes?.open} color="error" onClick={() => navigate(frontendRoutes.ADMIN_DISPUTES)} />
        <StatRow label="RESOLVED" count={disputes?.resolved} color="success" onClick={() => navigate(frontendRoutes.ADMIN_DISPUTES)} />
      </CardContent>
    </Card>
  </Grid>
);

const Analytics = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-analytics"],
    queryFn: getAdminAnalyticsSummary,
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Analytics</Typography>
      {data?.generatedAt && (
        <Typography variant="body2" color="text.secondary" mb={3}>
          As of {new Date(data.generatedAt).toLocaleString(undefined, { timeZoneName: "short" })}
        </Typography>
      )}

      {isLoading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">Failed to load analytics.</Alert>
      ) : (
        <Grid container spacing={2}>
          {ENTITY_CONFIG.map((config) => (
            <EntityCard key={config.key} config={config} breakdown={data?.[config.key]} navigate={navigate} />
          ))}
          <DisputesCard disputes={data?.disputes} navigate={navigate} />
        </Grid>
      )}
    </Box>
  );
};

export default Analytics;
